import { useLazyQuery } from '@apollo/client'
import gql from 'graphql-tag'
import { useCallback } from 'react'

// State type for the branch location
export type BranchState = {
	name?: string | null,
	abbreviation?: string | null,
}

// Branch location type based on the expected response.
// es-api returns zip_code as an integer (ESDB stores it that way), but the
// RestLink typePatcher in config/apollo-client.ts coerces it to a padded string
// before it reaches consumers, so this string type is accurate at read time.
export type BranchLocation = {
	street_1?: string | null,
	street_2?: string | null,
	city?: string | null,
	state?: BranchState | null,
	zip_code?: string | null,
}

// Branch type based on the expected response
export type Branch = {
	id: number,
	name?: string | null,
	location?: BranchLocation | null,
}

export type BranchPayload = {
	branch: Branch,
}

// GraphQL query using Apollo Link REST pattern
export const branchQuery = gql`
	query BranchQuery($branchId: String!) {
		branch(branchId: $branchId)
			@rest(
				type: "BranchPayload"
				path: "/branches/{args.branchId}"
				endpoint: "esapi"
			) {
			id
			name
			location
		}
	}
`

export type UseBranchQueryResult = {
	fetchBranch: ( branchId: string ) => void,
	branch: Branch | null,
	loading: boolean,
	error: any,
}

/**
 * Hook for querying branch data using Apollo Link REST
 * Provides branch information including location/address
 */
export default function useBranchQuery(): UseBranchQueryResult {
	const [ executeQuery, { data, loading, error } ] = useLazyQuery<BranchPayload>( branchQuery, {
		errorPolicy: 'all',
		fetchPolicy: 'cache-and-network',
	} )

	const fetchBranch = useCallback( ( branchId: string ) => {
		if ( !branchId ) {
			return
		}

		executeQuery( {
			variables: {
				branchId,
			},
			context: {
				headers: {
					'X-Fields': '{ id, name, location { street_1, street_2, city, zip_code, state { name, abbreviation } } }',
				},
			},
		} )
	}, [ executeQuery ] )

	return {
		fetchBranch,
		branch: data?.branch ?? null,
		loading,
		error,
	}
}
